import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ProductMenuFilter.module.less';
import { LEFT, BOTTOM } from '../Popup/alignments';
import { translate } from '../../services/L10n';
import Tooltip from '../Popup/Tooltip';
import { Icon } from '../Icon';
import {
  selectIsWallResizerActive,
  selectIsMeasurementsActive,
} from '../../state/scene/sceneSelectors';
import { selectIsSelectedFilter } from '../../state/productMenu/productMenuSelectors';
import {
  selectIsMobilePortrait,
  selectIsTabletPortrait,
} from '../../state/userAgent/userAgentSelectors';
import { thunkSetFilter } from '../../state/productMenu/productMenuThunks';
import { actionSetFilterIntroPopupBeVisible } from '../../state/popups/popupsActions';
import { Filter } from '../../state/productMenu/productMenuTypes';
import { actionSetWallResizerInactive } from '../../state/scene';
import { t } from '../../translations';

export interface Props {
  filter: Filter;
}

const ProductMenuFilter: React.FunctionComponent<Props> = ({ filter }) => {
  const wallResizerActive = useSelector(selectIsWallResizerActive);
  const measurementsActive = useSelector(selectIsMeasurementsActive);
  const isMobilePortrait = useSelector(selectIsMobilePortrait);
  const isTabletPortrait = useSelector(selectIsTabletPortrait);
  const isSelected = useSelector(selectIsSelectedFilter(filter));

  const dispatch = useDispatch();

  const setFilter = (filter: string) => dispatch(thunkSetFilter(filter));
  const setHasShownFilterIntroPopup = () =>
    dispatch(actionSetFilterIntroPopupBeVisible(false));

  /**
   * Close wallresizer or measurements if active
   */
  const closeActionButtons = () => {
    wallResizerActive &&
      dispatch(actionSetWallResizerInactive({ nonInteraction: true }));
  };

  /**
   * Set filter
   * @param filter
   * @returns {function(): *}
   * @private
   */
  const _setFilter =
    ({ name }: Filter) =>
    () => {
      setFilter(name);
      setHasShownFilterIntroPopup();
      closeActionButtons();
    };

  /**
   * Checks if filter is selected one
   * @returns {boolean}
   * @private
   */
  const _isSelected = () =>
    isSelected &&
    (!isMobilePortrait || (!measurementsActive && !wallResizerActive));

  /**
   * Return item classes
   * @returns {string}
   */
  const getItemClassNames = () =>
    classNames(styles.item, {
      [styles.selected]: _isSelected(),
    });

  return (
    <Tooltip
      key={filter.name}
      alignment={isTabletPortrait ? BOTTOM : LEFT}
      content={translate(t[filter.labelTranslationKey])}
    >
      <button className={getItemClassNames()} onClick={_setFilter(filter)}>
        <div className={classNames(styles.iconHolder)}>
          <Icon iconName={filter.iconName} />
        </div>
      </button>
    </Tooltip>
  );
};

export default ProductMenuFilter;
